import React from 'react';
import SEO from '../../components/SEO/SEO';
import { useLanguage } from '../../context/LanguageContext';
import { getDataProtectionPageStructuredData } from '../../utils/structuredData';
import './DataProtection.css';

const DataProtection = () => {
  const { lang, t } = useLanguage();
  const baseUrl = 'https://sentmo.com';
  const dp = t.dataProtection;
  const description = lang === 'de'
    ? "Datenschutzerklärung für Sentmo gemäß DSGVO. Erfahren Sie, wie wir Ihre persönlichen Daten schützen und verarbeiten."
    : "Privacy Policy for Sentmo in accordance with GDPR. Learn how we protect and process your personal data.";

  return (
    <>
      <SEO
        title={dp.title}
        description={description}
        keywords={lang === 'de'
          ? "Datenschutz, DSGVO, Privacy Policy, Datenschutzerklärung"
          : "Privacy, GDPR, Privacy Policy, Data Protection"
        }
        canonicalUrl={`${baseUrl}/${lang}/dataprotection`}
        lang={lang}
        alternateUrls={[
          { lang: 'de', url: `${baseUrl}/de/dataprotection` },
          { lang: 'en', url: `${baseUrl}/en/dataprotection` }
        ]}
        structuredData={getDataProtectionPageStructuredData({
          lang,
          title: dp.title,
          description
        })}
      />
      <div className="page data-protection-container">
        <div className="data-protection-content">
          <h1>{dp.title}</h1>

          {/* Section 1: Privacy at a Glance */}
          <section className="data-protection-section">
            <h2>{dp.section1.title}</h2>

            <h3>{dp.section1.generalTitle}</h3>
            <p>{dp.section1.generalText}</p>

            <h3>{dp.section1.dataCollectionTitle}</h3>

            <h4>{dp.section1.responsibleTitle}</h4>
            <p>{dp.section1.responsibleText}</p>

            <h4>{dp.section1.howCollectedTitle}</h4>
            <p>{dp.section1.howCollectedText1}</p>
            <p>{dp.section1.howCollectedText2}</p>

            <h4>{dp.section1.purposeTitle}</h4>
            <p>{dp.section1.purposeText}</p>

            <h4>{dp.section1.rightsTitle}</h4>
            <p>{dp.section1.rightsText}</p>
            <p>{dp.section1.contactText}</p>
          </section>

          {/* Section 2: Hosting */}
          <section className="data-protection-section">
            <h2>{dp.section2.title}</h2>
            <p>{dp.section2.text}</p>

            <h3>{dp.section2.externalTitle}</h3>
            <p>{dp.section2.externalText1}</p>
            <p>{dp.section2.externalText2}</p>
            <p>{dp.section2.externalText3}</p>
            <p>{dp.section2.hostingProvider}</p>
            <p>
              Cloudflare Germany GmbH<br />
              Rosental 7, c/o Mindspace, 80331 Munich
            </p>

            <h4>{dp.section2.dpaTitle}</h4>
            <p>{dp.section2.dpaText}</p>
          </section>

          {/* Section 3: General Information and Mandatory Information */}
          <section className="data-protection-section">
            <h2>{dp.section3.title}</h2>

            <h3>{dp.section3.privacyTitle}</h3>
            <p>{dp.section3.privacyText1}</p>
            <p>{dp.section3.privacyText2}</p>
            <p>{dp.section3.privacyText3}</p>

            <h3>{dp.section3.responsibleTitle}</h3>
            <p>{dp.section3.responsibleText}</p>
            <p>
              Sven Pflüger<br />
              Schusterstraße 40<br />
              79098 Freiburg im Breisgau
            </p>
            <p>
              {t.imprint.phone}: +49 176 41450810<br />
              {t.imprint.email}: business.pflueger@gmail.com
            </p>
            <p>{dp.section3.responsibleEntity}</p>

            <h3>{dp.section3.storageTitle}</h3>
            <p>{dp.section3.storageText}</p>

            <h3>{dp.section3.legalBasesTitle}</h3>
            <p>{dp.section3.legalBasesText}</p>

            <h3>{dp.section3.recipientsTitle}</h3>
            <p>{dp.section3.recipientsText}</p>

            <h3>{dp.section3.revocationTitle}</h3>
            <p>{dp.section3.revocationText}</p>

            <h3>{dp.section3.objectionTitle}</h3>
            <p>{dp.section3.objectionText1}</p>
            <p>{dp.section3.objectionText2}</p>

            <h3>{dp.section3.complaintTitle}</h3>
            <p>{dp.section3.complaintText}</p>

            <h3>{dp.section3.portabilityTitle}</h3>
            <p>{dp.section3.portabilityText}</p>

            <h3>{dp.section3.accessTitle}</h3>
            <p>{dp.section3.accessText}</p>

            <h3>{dp.section3.restrictionTitle}</h3>
            <p>{dp.section3.restrictionText}</p>
            <ul>
              {dp.section3.restrictionList.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p>{dp.section3.restrictionResult}</p>

            <h3>{dp.section3.sslTitle}</h3>
            <p>{dp.section3.sslText1}</p>
            <p>{dp.section3.sslText2}</p>

            <h3>{dp.section3.spamTitle}</h3>
            <p>{dp.section3.spamText}</p>
          </section>

          {/* Section 4: Data Collection on This Website */}
          <section className="data-protection-section">
            <h2>{dp.section4.title}</h2>

            <h3>{dp.section4.cookiesTitle}</h3>
            <p>{dp.section4.cookiesText1}</p>
            <p>{dp.section4.cookiesText2}</p>
            <p>{dp.section4.cookiesText3}</p>
            <p>{dp.section4.cookiesText4}</p>
            <p>{dp.section4.cookiesText5}</p>

            <h3>{dp.section4.serverLogsTitle}</h3>
            <p>{dp.section4.serverLogsText1}</p>
            <ul>
              {dp.section4.serverLogsList.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p>{dp.section4.serverLogsText2}</p>
            <p>{dp.section4.serverLogsText3}</p>
          </section>

          {/* Section 5: Analytics Tools and Advertising */}
          <section className="data-protection-section">
            <h2>{dp.section5.title}</h2>

            <h3>{dp.section5.gaTitle}</h3>
            <p>{dp.section5.gaText1}</p>
            <p>{dp.section5.gaText2}</p>
            <p>{dp.section5.gaText3}</p>
            <p>{dp.section5.gaText4}</p>
            <p>{dp.section5.gaText5}</p>

            <h4>{dp.section5.gaIpTitle}</h4>
            <p>{dp.section5.gaIpText}</p>

            <h4>{dp.section5.gaBrowserTitle}</h4>
            <p>{dp.section5.gaBrowserText}</p>

            <p>{dp.section5.gaMoreInfo}</p>

            <h4>{dp.section5.gaDpaTitle}</h4>
            <p>{dp.section5.gaDpaText}</p>
          </section>

          {/* Source */}
          <section className="data-protection-section">
            <p className="source">
              {dp.source}: <a href="https://www.e-recht24.de" target="_blank" rel="noopener noreferrer">https://www.e-recht24.de</a>
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default DataProtection;
